import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  HostListener,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';

import { PlanoAlimentar } from '../../../models/PlanoAlimentar';
import { PlanoRefeicao } from '../../../models/PlanoRefeicao';
import { PlanoRefeicaoAlimento } from '../../../models/PlanoRefeicaoAlimento';
import { PlanoRefeicaoOpcao } from '../../../models/PlanoRefeicaoOpcao';
import { PlanoStatus } from '../../../models/PlanoStatus';
import { TacoAlimento } from '../../../models/TacoAlimento';
import { PlanoAlimentarService } from '../../../services/plano-alimentar/plano-alimentar.service';
import { SnackbarService } from '../../../services/snackbar/snackbar.service';
import { ModalFinalizacaoComponent } from '../../shared/modal-finalizacao/modal-finalizacao.component';
import { AdicionarRefeicaoModal } from './dialogs/adicionar-refeicao-modal/adicionar-refeicao-modal';
import { PesquisaAlimentosTaco } from './dialogs/pesquisa-alimentos-taco/pesquisa-alimentos-taco';
import { VisualizarObservacoesPlano } from './dialogs/visualizar-observacoes-plano/visualizar-observacoes-plano';
import { DetalhesAlimentoTaco } from './dialogs/detalhes-alimento-taco/detalhes-alimento-taco';

interface AlimentoLinha {
  taco: TacoAlimento;
  plano: PlanoRefeicaoAlimento;
  editandoNome?: boolean;
  editandoQuantidade?: boolean;
  editandoKcalorias?: boolean;
  editandoProteinas?: boolean;
  editandoCarboidratos?: boolean;
  editandoLipidios?: boolean;
  opcao: PlanoRefeicaoOpcao;
}

@Component({
  selector: 'app-plano-alimentar-refeicoes',
  imports: [
    CommonModule,
    MatIconModule,
    MatDialogModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatSelectModule,
  ],
  templateUrl: './plano-alimentar-refeicoes.html',
  styleUrl: './plano-alimentar-refeicoes.scss',
})
export class PlanoAlimentarRefeicoes implements OnInit {
  @Input() planoSelecionado: PlanoAlimentar = new PlanoAlimentar();
  @Output() planoSalvo = new EventEmitter<PlanoAlimentar>();

  private planoOriginal?: PlanoAlimentar;
  nomesRefeicoesSelecionadas: string[] = [];
  refeicoesSelecionadas: PlanoRefeicao[] = [];
  idRefeicaoAberta: number | null = null;
  modoEdicao!: boolean;
  containerAguaVisivel!: boolean;
  quantidadesAgua: number[] = [];
  alimentosPorRefeicao = new Map<number, Map<number, AlimentoLinha[]>>();

  private dirty = false;
  public hasPendingChanges(): boolean {
    return this.dirty;
  }

  public setDirtyTrue() {
    this.dirty = true;
  }

  private clearDirty() {
    this.dirty = false;
  }

  private proximoIdRefeicao = 1;
  private proximoIdRefeicaoAlimento = 1;

  constructor(
    private dialog: MatDialog,
    private snackBarService: SnackbarService,
    private planoAlimentarService: PlanoAlimentarService,
  ) {}

  async ngOnInit(): Promise<void> {
    this.planoOriginal = JSON.parse(JSON.stringify(this.planoSelecionado));
    this.planoSelecionado?.status == PlanoStatus.ATIVO
      ? (this.modoEdicao = true)
      : (this.modoEdicao = false);

    if (this.planoSelecionado?.refeicoes?.length) {
      this.refeicoesSelecionadas = this.planoSelecionado.refeicoes;
      this.nomesRefeicoesSelecionadas = this.refeicoesSelecionadas.map((r) =>
        r.nome === 'Outra' ? r.nomePersonalizado || 'Outra' : r.nome,
      );

      for (const refeicao of this.refeicoesSelecionadas) {
        if (!refeicao.idRefeicao) {
          refeicao.idRefeicao = this.proximoIdRefeicao++;
        }

        refeicao.opcoes = refeicao.opcoes || [];
        const mapaOpcoes = new Map<number, AlimentoLinha[]>();

        for (const opcao of refeicao.opcoes) {
          if (!opcao.idOpcao) {
            opcao.idOpcao = refeicao.opcoes.indexOf(opcao) + 1;
          }

          const alimentosLinha: AlimentoLinha[] = [];

          for (const alimento of opcao.alimentos || []) {
            try {
              if (alimento.idTaco === null) {
                alimentosLinha.push({
                  taco: {} as TacoAlimento,
                  plano: alimento,
                  editandoNome: false,
                  editandoQuantidade: false,
                  editandoKcalorias: false,
                  editandoProteinas: false,
                  editandoCarboidratos: false,
                  editandoLipidios: false,
                  opcao,
                });
              } else {
                const taco =
                  await this.planoAlimentarService.detalharAlimentoTaco(
                    alimento.idTaco,
                  );
                alimentosLinha.push({
                  taco,
                  plano: alimento,
                  opcao,
                  editandoNome: false,
                  editandoQuantidade: false,
                });
              }
            } catch (e) {
              console.error(
                `Erro ao buscar alimento TACO ${alimento.idTaco}:`,
                e,
              );
            }
          }

          mapaOpcoes.set(opcao.idOpcao, alimentosLinha);
        }

        this.alimentosPorRefeicao.set(refeicao.idRefeicao, mapaOpcoes);
      }
    } else {
      this.refeicoesSelecionadas = [];
      this.nomesRefeicoesSelecionadas = [];
      this.alimentosPorRefeicao.clear();
    }

    if (!this.planoSelecionado.consumoAguaMl) {
      this.idRefeicaoAberta = -1;
      this.containerAguaVisivel = true;
    }

    this.quantidadesAgua = Array.from(
      { length: (6000 - 600) / 200 + 1 },
      (_, i) => 600 + i * 200,
    );
    this.clearDirty();
  }

  private confirmarAcao(
    titulo: string,
    conteudo: string,
    acao: string,
    callback: () => void,
  ) {
    const dialogRef = this.dialog.open(ModalFinalizacaoComponent, {
      width: '600px',
      data: { isAviso: true, titulo, conteudo, possuiAcoes: true, acao },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) callback();
    });
  }

  private atualizarMapaAlimentos(
    refeicaoId: number,
    opcaoId: number,
    linhas: AlimentoLinha[],
  ) {
    let mapaOpcoes =
      this.alimentosPorRefeicao.get(refeicaoId) ||
      new Map<number, AlimentoLinha[]>();
    mapaOpcoes.set(opcaoId, linhas);
    this.alimentosPorRefeicao.set(refeicaoId, mapaOpcoes);
  }

  private obterLinhas(refeicaoId: number, opcaoId: number): AlimentoLinha[] {
    return this.alimentosPorRefeicao.get(refeicaoId)?.get(opcaoId) || [];
  }

  private removerOpcaoDoMapa(refeicaoId: number, opcaoId: number) {
    const mapaOpcoes = this.alimentosPorRefeicao.get(refeicaoId);
    if (mapaOpcoes) {
      mapaOpcoes.delete(opcaoId);
      this.alimentosPorRefeicao.set(refeicaoId, mapaOpcoes);
    }
  }

  private criarPlanoRefeicaoAlimento(
    taco: TacoAlimento,
  ): PlanoRefeicaoAlimento {
    const plano = new PlanoRefeicaoAlimento();
    plano.idRefeicaoAlimento = this.proximoIdRefeicaoAlimento++;
    plano.idTaco = taco.id;
    plano.nomeOriginal = taco.descricaoAlimento;
    plano.nomePersonalizado = taco.descricaoAlimento;
    plano.gramas = 100;
    return plano;
  }

  private criarPlanoRefeicaoPesonalizado(
    nome: string,
    gramas: number,
    kcalorias: number,
    proteinas: number,
    carboidratos: number,
    lipidios: number,
  ): PlanoRefeicaoAlimento {
    const plano = new PlanoRefeicaoAlimento();
    plano.idRefeicaoAlimento = this.proximoIdRefeicaoAlimento++;
    plano.idTaco = 0; // ID 0 para indicar que é um alimento personalizado
    plano.nomeOriginal = nome;
    plano.nomePersonalizado = nome;

    plano.gramas = gramas;
    plano.kcaloriasPersonalizado = kcalorias;
    plano.proteinasPersonalizado = proteinas;
    plano.carboidratosPersonalizado = carboidratos;
    plano.lipidiosPersonalizado = lipidios;

    return plano;
  }

  private atualizarPlanoOriginal() {
    this.planoOriginal = JSON.parse(JSON.stringify(this.planoSelecionado));
  }

  toggleAgua() {
    this.idRefeicaoAberta = -1;
    this.containerAguaVisivel = !this.containerAguaVisivel;
  }

  toggleRefeicao(refeicao: PlanoRefeicao) {
    this.idRefeicaoAberta =
      this.idRefeicaoAberta === refeicao.idRefeicao
        ? null
        : (refeicao.idRefeicao ?? null);
  }

  refeicaoAberta(refeicao: PlanoRefeicao): boolean {
    return this.idRefeicaoAberta === refeicao.idRefeicao;
  }

  abrirModalRefeicoes() {
    const dialogRef = this.dialog.open(AdicionarRefeicaoModal, {
      width: '700px',
      data: this.nomesRefeicoesSelecionadas,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result) return;

      result.idRefeicao = this.proximoIdRefeicao++;
      this.refeicoesSelecionadas = [...this.refeicoesSelecionadas, result];
      this.idRefeicaoAberta = result.idRefeicao;

      if (result.nome !== 'Outra') {
        this.nomesRefeicoesSelecionadas.push(result.nome);
      }

      this.planoSelecionado.refeicoes = this.refeicoesSelecionadas;
      this.setDirtyTrue();
    });
  }

  abrirModalDetalhesAlimentoTaco(
    idAlimento: number,
    gramas: number | undefined,
  ) {
    const dialogRef = this.dialog.open(DetalhesAlimentoTaco, {
      width: '1000px',
      data: {
        idAlimento: idAlimento,
        gramas: gramas,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result) return;
    });
  }

  removerRefeicao(refeicao: PlanoRefeicao) {
    this.confirmarAcao(
      'Remover refeição',
      'Ao clicar em remover, a refeição selecionada será removida desse plano alimentar.',
      'Remover',
      () => {
        this.refeicoesSelecionadas = this.refeicoesSelecionadas.filter(
          (r) => r !== refeicao,
        );
        this.alimentosPorRefeicao.delete(refeicao.idRefeicao!);
        this.nomesRefeicoesSelecionadas =
          this.nomesRefeicoesSelecionadas.filter(
            (nome) => nome !== refeicao.nome,
          );
        if (this.idRefeicaoAberta === refeicao.idRefeicao)
          this.idRefeicaoAberta = null;

        this.planoSelecionado.refeicoes = this.refeicoesSelecionadas;
        this.setDirtyTrue();
      },
    );
  }

  adicionarOpcao(refeicao: PlanoRefeicao) {
    refeicao.opcoes = refeicao.opcoes || [];

    if (refeicao.opcoes.length >= 4) {
      this.snackBarService.error('Cada refeição só pode ter até 4 opções.');
      return;
    }

    const novaOpcao: PlanoRefeicaoOpcao = {
      idOpcao: refeicao.opcoes.length + 1,
      titulo: `Opção ${refeicao.opcoes.length + 1}`,
      alimentos: [],
    };

    refeicao.opcoes.push(novaOpcao);
    this.setDirtyTrue();
  }

  removerOpcao(refeicao: PlanoRefeicao, opcao: PlanoRefeicaoOpcao) {
    this.confirmarAcao(
      'Remover opção',
      'Ao clicar em remover, a opção selecionada será removida desse plano alimentar.',
      'Remover',
      () => {
        refeicao.opcoes = refeicao.opcoes!.filter(
          (o) => o.idOpcao !== opcao.idOpcao,
        );
        this.removerOpcaoDoMapa(refeicao.idRefeicao!, opcao.idOpcao!);

        refeicao.opcoes!.forEach((op, i) => {
          op.titulo = `Opção ${i + 1}`;
        });

        this.refeicoesSelecionadas = [...this.refeicoesSelecionadas];
        this.setDirtyTrue();
      },
    );
  }

  abrirModalBuscarAlimentos(
    opcao: PlanoRefeicaoOpcao,
    refeicao: PlanoRefeicao,
  ) {
    const dialogRef = this.dialog.open(PesquisaAlimentosTaco, {
      width: '500px',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result) return;
      if (result === 'MANUAL') {
        const planoAlimento = this.criarPlanoRefeicaoPesonalizado(
          'Nome personalizado',
          100,
          0,
          0,
          0,
          0,
        );

        opcao.alimentos = opcao.alimentos || [];
        opcao.alimentos.push(planoAlimento);

        const novaLinha: AlimentoLinha = {
          taco: {} as TacoAlimento,
          plano: planoAlimento,
          editandoNome: true,
          editandoQuantidade: true,
          editandoKcalorias: true,
          editandoProteinas: true,
          editandoCarboidratos: true,
          editandoLipidios: true,
          opcao,
        };

        const linhas = this.obterLinhas(refeicao.idRefeicao!, opcao.idOpcao!);
        linhas.push(novaLinha);
        this.atualizarMapaAlimentos(
          refeicao.idRefeicao!,
          opcao.idOpcao!,
          linhas,
        );
        this.setDirtyTrue();
      } else {
        const planoAlimento = this.criarPlanoRefeicaoAlimento(result);
        opcao.alimentos = opcao.alimentos || [];
        opcao.alimentos.push(planoAlimento);

        const novaLinha: AlimentoLinha = {
          taco: result,
          plano: planoAlimento,
          editandoNome: true,
          editandoQuantidade: true,
          editandoKcalorias: false,
          editandoProteinas: false,
          editandoCarboidratos: false,
          editandoLipidios: false,
          opcao,
        };

        const linhas = this.obterLinhas(refeicao.idRefeicao!, opcao.idOpcao!);
        linhas.push(novaLinha);
        this.atualizarMapaAlimentos(
          refeicao.idRefeicao!,
          opcao.idOpcao!,
          linhas,
        );
        this.setDirtyTrue();
      }
    });
  }

  removerAlimento(linha: AlimentoLinha, refeicao: PlanoRefeicao) {
    this.confirmarAcao(
      'Remover alimento',
      'Ao clicar em remover, o alimento selecionado será removido dessa opção de refeição.',
      'Remover',
      () => {
        const opcao = linha.opcao;
        opcao.alimentos = opcao.alimentos?.filter(
          (a) => a.idRefeicaoAlimento !== linha.plano.idRefeicaoAlimento,
        );

        const mapaOpcoes =
          this.alimentosPorRefeicao.get(refeicao.idRefeicao!) ||
          new Map<number, AlimentoLinha[]>();
        const linhas = (mapaOpcoes.get(opcao.idOpcao!) || []).filter(
          (l) => l.plano.idRefeicaoAlimento !== linha.plano.idRefeicaoAlimento,
        );
        mapaOpcoes.set(opcao.idOpcao!, linhas);
        this.alimentosPorRefeicao.set(refeicao.idRefeicao!, mapaOpcoes);

        this.refeicoesSelecionadas = [...this.refeicoesSelecionadas];
        this.setDirtyTrue();
      },
    );
  }

  abrirModalObservacoes(refeicao: PlanoRefeicao) {
    const observacoesPlano = this.planoSelecionado?.observacoes ?? '';
    const observacoesRefeicao = refeicao?.sugestoesViaApp ?? '';

    const todasObservacoes: string[] = [
      ...(Array.isArray(observacoesRefeicao)
        ? observacoesRefeicao
        : typeof observacoesRefeicao === 'string'
          ? observacoesRefeicao
              .split(';')
              .map((o) => o.trim())
              .filter((o) => o.length > 0)
          : []),
      ...(observacoesPlano
        ? observacoesPlano
            .split(';')
            .map((o) => o.trim())
            .filter((o) => o.length > 0)
        : []),
    ];

    this.dialog.open(VisualizarObservacoesPlano, {
      width: '500px',
      data: { observacoes: todasObservacoes },
    });
  }

  editarNome(linha: AlimentoLinha) {
    linha.editandoNome = true;
  }

  confirmarNome(linha: AlimentoLinha) {
    linha.editandoNome = false;
    const alimento = linha.opcao.alimentos?.find(
      (a) => a.idRefeicaoAlimento === linha.plano.idRefeicaoAlimento,
    );
    if (alimento) alimento.nomePersonalizado = linha.plano.nomePersonalizado;
    this.setDirtyTrue();
  }

  editarQuantidade(linha: AlimentoLinha) {
    linha.editandoQuantidade = true;
  }

  editarKcalorias(linha: AlimentoLinha) {
    linha.editandoKcalorias = true;
  }

  editarProteinas(linha: AlimentoLinha) {
    linha.editandoProteinas = true;
  }

  editarCarboidratos(linha: AlimentoLinha) {
    linha.editandoCarboidratos = true;
  }

  editarLipidios(linha: AlimentoLinha) {
    linha.editandoLipidios = true;
  }

  confirmarQuantidade(linha: AlimentoLinha) {
    linha.editandoQuantidade = false;
    const alimento = linha.opcao.alimentos?.find(
      (a) => a.idRefeicaoAlimento === linha.plano.idRefeicaoAlimento,
    );
    if (alimento) alimento.gramas = linha.plano.gramas;
    this.setDirtyTrue();
  }

  confirmarKcalorias(linha: AlimentoLinha) {
    linha.editandoKcalorias = false;
    const alimento = linha.opcao.alimentos?.find(
      (a) => a.idRefeicaoAlimento === linha.plano.idRefeicaoAlimento,
    );
    if (alimento)
      alimento.kcaloriasPersonalizado = linha.plano.kcaloriasPersonalizado;
    this.setDirtyTrue();
  }

  confirmarProteinas(linha: AlimentoLinha) {
    linha.editandoProteinas = false;
    const alimento = linha.opcao.alimentos?.find(
      (a) => a.idRefeicaoAlimento === linha.plano.idRefeicaoAlimento,
    );
    if (alimento)
      alimento.proteinasPersonalizado = linha.plano.proteinasPersonalizado;
    this.setDirtyTrue();
  }

  confirmarCarboidratos(linha: AlimentoLinha) {
    linha.editandoCarboidratos = false;
    const alimento = linha.opcao.alimentos?.find(
      (a) => a.idRefeicaoAlimento === linha.plano.idRefeicaoAlimento,
    );
    if (alimento)
      alimento.carboidratosPersonalizado =
        linha.plano.carboidratosPersonalizado;
    this.setDirtyTrue();
  }

  confirmarLipidios(linha: AlimentoLinha) {
    linha.editandoLipidios = false;
    const alimento = linha.opcao.alimentos?.find(
      (a) => a.idRefeicaoAlimento === linha.plano.idRefeicaoAlimento,
    );
    if (alimento)
      alimento.lipidiosPersonalizado = linha.plano.lipidiosPersonalizado;
    this.setDirtyTrue();
  }

  getValorNutriente(
    linha: AlimentoLinha,
    campo: 'energiaKcal' | 'proteinaG' | 'carboidratoG' | 'lipidiosG',
  ): number {
    const valor = this.parseNumero(linha.taco[campo]);
    const quantidade = linha.plano.gramas ?? 100;
    return valor * (quantidade / 100);
  }

  private parseNumero(valor?: string): number {
    if (!valor) return 0;
    const num = Number(valor.replace(',', '.'));
    return isNaN(num) ? 0 : num;
  }

  trackByOpcao(index: number, opcao: PlanoRefeicaoOpcao) {
    return opcao.idOpcao ?? index;
  }

  trackByLinha(index: number, linha: AlimentoLinha) {
    return linha.plano?.idRefeicaoAlimento ?? index;
  }

  planoValido(): boolean {
    if (!this.refeicoesSelecionadas.length) return false;

    for (const refeicao of this.refeicoesSelecionadas) {
      if (!refeicao.opcoes || !refeicao.opcoes.length) return false;

      for (const opcao of refeicao.opcoes) {
        if (!opcao.alimentos || !opcao.alimentos.length) return false;

        const linhas = this.obterLinhas(refeicao.idRefeicao!, opcao.idOpcao!);

        const alimentosConfirmados = opcao.alimentos.every((a) => {
          const linha = linhas.find(
            (l) => l.plano.idRefeicaoAlimento === a.idRefeicaoAlimento,
          );
          const nomeOk = linha
            ? !linha.editandoNome && a.nomePersonalizado?.trim()
            : a.nomePersonalizado?.trim();

          const qtdOk = linha
            ? !linha.editandoQuantidade && a.gramas && a.gramas > 0
            : a.gramas && a.gramas > 0;

          const isAlimentoPersonalizado = a.idTaco === 0 || a.idTaco === null;

          const kcalOk = isAlimentoPersonalizado
            ? linha
              ? !linha.editandoKcalorias && a.kcaloriasPersonalizado != null
              : a.kcaloriasPersonalizado != null
            : true;

          const proteinaOk = isAlimentoPersonalizado
            ? linha
              ? !linha.editandoProteinas && a.proteinasPersonalizado != null
              : a.proteinasPersonalizado != null
            : true;

          const carboidratoOk = isAlimentoPersonalizado
            ? linha
              ? !linha.editandoCarboidratos &&
                a.carboidratosPersonalizado != null
              : a.carboidratosPersonalizado != null
            : true;

          const lipidiosOk = isAlimentoPersonalizado
            ? linha
              ? !linha.editandoLipidios && a.lipidiosPersonalizado != null
              : a.lipidiosPersonalizado != null
            : true;

          return (
            nomeOk &&
            qtdOk &&
            kcalOk &&
            proteinaOk &&
            carboidratoOk &&
            lipidiosOk
          );
        });

        if (!alimentosConfirmados) return false;
      }
    }

    return true;
  }

  planoAlterado(): boolean {
    if (!this.modoEdicao || !this.planoOriginal) return false;

    // Cria cópias profundas para comparação
    const atual = JSON.parse(
      JSON.stringify({
        ...this.planoSelecionado,
        refeicoes: this.planoSelecionado.refeicoes?.map((r) => ({
          ...r,
          opcoes: r.opcoes?.map((o) => ({
            ...o,
            alimentos: o.alimentos?.map((a) => ({
              idRefeicaoAlimento: a.idRefeicaoAlimento,
              idTaco: a.idTaco,
              nomePersonalizado: a.nomePersonalizado,
              gramas: a.gramas,
            })),
          })),
        })),
      }),
    );

    const original = JSON.parse(
      JSON.stringify({
        ...this.planoOriginal,
        refeicoes: this.planoOriginal.refeicoes?.map((r) => ({
          ...r,
          opcoes: r.opcoes?.map((o) => ({
            ...o,
            alimentos: o.alimentos?.map((a) => ({
              idRefeicaoAlimento: a.idRefeicaoAlimento,
              idTaco: a.idTaco,
              nomePersonalizado: a.nomePersonalizado,
              gramas: a.gramas,
            })),
          })),
        })),
      }),
    );

    // Remove propriedades temporárias que não devem ser comparadas
    delete atual['idPlanoAlimentar'];
    delete original['idPlanoAlimentar'];

    return JSON.stringify(atual) !== JSON.stringify(original);
  }

  formatarNumero(valor: any): string {
    if (valor == null || valor === '') return '';
    const numero = parseFloat(valor);
    return Number.isInteger(numero)
      ? numero.toString()
      : numero.toFixed(2).replace(/\.?0+$/, '');
  }

  salvarPlano() {
    if (!this.planoValido()) {
      this.snackBarService.error(
        'Preencha todas as refeições com pelo menos uma opção e um alimento confirmado.',
      );
      return;
    }

    const titulo = this.modoEdicao
      ? 'Salvar alterações do plano'
      : 'Ativar plano alimentar no app';
    const texto = !this.modoEdicao
      ? 'Ao clicar em ativar, o plano alimentar será cadastrado ' +
        'e ativado no aplicativo para a pessoa. Deseja ativar o plano ' +
        'alimentar?'
      : 'Ao clicar em salvar alterações, todas as alterações ' +
        'serão salvas e exibidas no aplicativo para a pessoa. ' +
        'Deseja salvar as alterações?';
    const acao = this.modoEdicao ? 'Salvar' : 'Ativar';

    const dialogRef = this.dialog.open(ModalFinalizacaoComponent, {
      width: '600px',
      data: {
        titulo: titulo,
        conteudo: texto,
        possuiAcoes: true,
        acao: acao,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        this.planoSelecionado.consumoAguaMl =
          this.planoSelecionado.consumoAguaMl || 0;
        this.planoSelecionado.refeicoes = this.refeicoesSelecionadas;
        this.planoSelecionado.status = PlanoStatus.ATIVO;

        if (this.planoSelecionado.idPlanoAlimentar) {
          this.planoAlimentarService.editarPlanoAlimentar(
            this.planoSelecionado.idPessoa!,
            this.planoSelecionado,
          );
        } else {
          this.planoAlimentarService.salvarPlanoAlimentar(
            this.planoSelecionado.idPessoa!,
            this.planoSelecionado,
          );
        }

        this.clearDirty();
        this.voltarAoComponentePai();
      }
    });
  }

  sairSemSalvar() {
    const dialogRef = this.dialog.open(ModalFinalizacaoComponent, {
      width: '600px',
      data: {
        titulo: 'Sair sem salvar',
        isAviso: true,
        conteudo:
          'Ao sair sem salvar, você perderá todo o progresso ' +
          'e as informações preenchidas até o momento. Deseja sair?',
        possuiAcoes: true,
        acao: 'Sair sem salvar',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) this.voltarAoComponentePai();
    });
  }

  salvarRascunho() {
    if (!this.planoValido()) {
      this.snackBarService.error(
        'Preencha todas as refeições com pelo menos uma opção e um alimento confirmado.',
      );
      return;
    }

    const dialogRef = this.dialog.open(ModalFinalizacaoComponent, {
      width: '600px',
      data: {
        titulo: 'Salvar como rascunho',
        conteudo:
          'O plano alimentar foi salvo como rascunho. As informações atuais serão ' +
          'armazenadas, você poderá retornar e finalizar o preenchimento a ' +
          'qualquer momento, sem perder o que já foi registrado',
        possuiAcoes: false,
        acao: 'Sair sem salvar',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      this.planoSelecionado.consumoAguaMl =
        this.planoSelecionado.consumoAguaMl || 0;
      this.planoSelecionado.refeicoes = this.refeicoesSelecionadas;
      this.planoSelecionado.status = PlanoStatus.RASCUNHO;

      if (this.planoSelecionado.idPlanoAlimentar) {
        this.planoAlimentarService.editarPlanoAlimentar(
          this.planoSelecionado.idPessoa!,
          this.planoSelecionado,
        );
      } else {
        this.planoAlimentarService.salvarPlanoAlimentar(
          this.planoSelecionado.idPessoa!,
          this.planoSelecionado,
        );
      }
      this.clearDirty();
      this.voltarAoComponentePai();
    });
  }

  voltarAoComponentePai() {
    setTimeout(() => {
      this.planoSalvo.emit(this.planoSelecionado);
    }, 500);
  }

  @HostListener('window:beforeunload', ['$event'])
  beforeUnload(e: BeforeUnloadEvent) {
    if (this.hasPendingChanges()) {
      e.preventDefault();
      e.returnValue = '';
    }
  }
}
